import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TslToastrService } from '@tesla/ng-tds';
import { BankEditComponent } from 'app/common/bank/bank-edit.component';
import { BankUploadFileModel } from 'app/common/bank/bank-upload-file.model';
import { BankCountryConfig } from 'app/profile/models/bank.model';
import { GeneralSettings, KeyValuePair } from 'app/profile/models/generic.model';
import { OnboardingDocument } from 'app/profile/models/onboarding.model';
import { BankConfiguration, BankValidationRequest, Currency, SupplierBank, SupplierDocument } from 'app/profile/models/supplier.model';
import { BankService } from 'app/profile/services/bank.service';
import { SupplierStateService } from 'app/profile/services/supplierstate.service';
import { SupplierProfileState } from 'app/profile/store/app.reducer';
import { Observable, of } from 'rxjs';
import * as LoaderActions from "../../../../../shared/store/actions/loader.actions";
import { FileUploadService } from 'app/common/services/fileUpload.service';
import { I18nService } from 'app/shared/services/i18n.service';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-bank-task-dialog',
  templateUrl: './bank-task-dialog.component.html',
  styleUrls: ['./bank-task-dialog.component.scss']
})
export class BankTaskDialogComponent implements OnInit {

  @ViewChild('bankEdit', { static: false }) bankEditComponent: BankEditComponent;
  
  supplierCode: string;
  requiredDocuments: OnboardingDocument;
  supplierBank: SupplierBank;
  generalSettings$: Observable<GeneralSettings>;
  countries$: Observable<BankCountryConfig[]>;
  currencies$: Observable<Currency[]>;
  bankConfig$: Observable<BankConfiguration>;
  paymentOrigins$: Observable<KeyValuePair<string, string>[]>;
  intermediaryPaymentOrigins$: Observable<KeyValuePair<string, string>[]>;
  showPaymentOriginControl: boolean = false;
  selectedCountry: string;
  selectedPaymentOrigin: string;
  disableSensitiveFieldModification: boolean = false;
  readOnly: boolean = false;
  legalName: string;
  beneficiaryNameDifferentReasons: string[];
  requestType:string = null;
  isSecurityCheckRequired: boolean = false;
  requestId: string;

  constructor(
    public dialogRef: MatDialogRef<BankTaskDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    private toaster: TslToastrService,
    private supplierStateService: SupplierStateService,
    private bankService: BankService,
    private supplierStore: Store<SupplierProfileState>,
    private fileUploadService: FileUploadService,
    private translate: I18nService,
  ) { }

  ngOnInit(): void {
    if(this.data) {
      this.supplierCode = this.data?.supplierCode;
      this.disableSensitiveFieldModification = this.data?.disableSensitiveFieldModification;
      this.requiredDocuments = this.data?.requiredDocuments;
      this.supplierBank = this.data?.supplierBank;
      this.generalSettings$ = this.data?.generalSettings$;
      this.countries$ = this.data?.countries$;
      this.currencies$ = this.data?.currencies$;
      this.bankConfig$ = this.data?.bankConfig$;
      this.intermediaryPaymentOrigins$ = this.data?.intermediaryPaymentOrigins$;
      this.showPaymentOriginControl = this.data?.showPaymentOriginControl;
      this.readOnly = this.data?.readOnly;
      this.legalName = this.data?.legalName;
      this.beneficiaryNameDifferentReasons = [
        "Company acquisition, reorganization, or merger",
        "Factoring Agent or third-party financial services",
        "Parent, child or sister company used for accounts receivable",
        "Other, please provide details"
      ],
      this.requestType = this.data?.requestType;
      this.requestId = this.data?.requestId;
    }
  }

  setErrorMessage(message) {
    if (message) this.toaster.error((message && message.trim() != '') ? message : this.translate.get('Something went wrong!'));
  }

  /** Requests bank configuration each time the country chang */
  countryChangeEventHandler(params: { countryCode: string }) {
    //  Get the bank configuration for the selected country
    if(this.showPaymentOriginControl) {
      this.selectedCountry = params.countryCode;
      this.paymentOrigins$ = this.supplierStateService.getPaymentOrigins(params.countryCode)?.pipe(map(x => {
        x.forEach(pair => pair.Key = this.translate.get(pair.Key));
        return x;
      }));
    } else this.bankConfig$ = this.supplierStateService.getBankConfigurationForOnboarding(this.supplierCode, params.countryCode, this.requestId);
  }

  onPaymentOriginChangedEvent(params: { paymentOrigin: string }) {
    if(this.showPaymentOriginControl) {
      this.selectedPaymentOrigin = params.paymentOrigin;
      this.bankConfig$ = this.supplierStateService.getBankConfiguration(this.selectedCountry, this.selectedPaymentOrigin);
    }
  }

  verifyEventHandler(supplierBank: SupplierBank) {
    this.bankService.getVerificationKeys(this.supplierCode)
      .subscribe((response) => {
        var argsAcc = this.bankService.getVerifyArgs(response.signInToken, response.instrumentReference);
        this.bankEditComponent.payoutRefAngular.verifyAccount(argsAcc)
          .then(res => {
            if (res.decision == 'ACCEPT') {
              // we are receiving bpm events for deposit confirmation so skipping the UI call.
              // this.onCompleteEvent.emit();
              this.supplierBank.VerificationStatus = 'VERIFIED';
            }
          })
          .catch(ex => {
            this.toaster.error(this.translate.get('Something went wrong!'));
          })
      });
  }

  submitEventHandler(supplierBank: SupplierBank, uploadFiles: BankUploadFileModel[], isPartialSave: boolean, isBankDataChanged?: boolean) {

    if((isBankDataChanged != undefined || isBankDataChanged != null) && isBankDataChanged == true)
    {
      this.isSecurityCheckRequired = true
    }
    else
    {
      this.isSecurityCheckRequired = false;
    }

   this.sendValidationRequest(supplierBank)
      // .pipe(takeUntil(this.ngUnsubscribe))

      //  Validation request returns a collection of errors if there are any.
      //  If we don't have any error, upload the files to the server,
      //  If we do have errors, then set the error message.
      .mergeMap(errors => {
        if (!errors || errors.length <= 0) {
          let filesToUpload = uploadFiles.filter(uf => uf.fileData != null);
          let existingFiles = uploadFiles.filter(uf => uf.fileData == null)?.map(file => file.supplierDocument);

          if (filesToUpload.length > 0) {

            //  Upload files
            return this.fileUploadService.uploadFileToServer(filesToUpload.map(uf => uf.fileData), this.supplierCode)
              .map(newFiles => {
                return [newFiles, ...existingFiles]
              });
          } else {

            //  Return existing files as an observable
            return of(existingFiles);
          }
        }

        //  Othersise set error message and return null
        this.setErrorMessage(errors.join('; '));
        this.supplierStore.dispatch(new LoaderActions.DecrementLoaderCount());
        return of(null);
      })

      //  Once, the upload file action is complete, then send post to save step status
      .map(resp => {
        if (resp) {

          //  Cast the response as a collection of supplier documents
          let docs = Array.isArray(resp) ? resp as SupplierDocument[] : [resp as SupplierDocument];

          //  Append the list of document IDs to the supplier bank
          supplierBank.Documents = docs.map(doc => doc['documentID'] || doc.DocumentID)?.filter(doc => doc != null);
          supplierBank.DocumentInfos = docs.map(doc => ({
            DocumentID: doc['documentID'] || doc.DocumentID,
            FileName: doc['fileName'] || doc.FileName,
            DocumentVersion: doc['documentVersion'] || doc.DocumentVersion,
            UploadedDate: doc['modifiedDate'] || doc.ModifiedDate,
            Mode: 'Upload',
            TypeID: this.requiredDocuments?.TypeID,
            DocumentType: this.requiredDocuments?.DocumentType ?? doc.DocumentType,
            DocumentTypeDescription: this.requiredDocuments?.DocumentTypeDescription,
            IsDocumentAvailableForSupplier : this.requiredDocuments?.IsDocumentAvailableForSupplier,
            AssociatedCategoryLinkId : this.requiredDocuments?.AssociatedCategoryLinkId
          }));

          //  Map the effective and expiry dates
          supplierBank.DocumentInfos.forEach(doc => {
            let file = uploadFiles.find(file => file.fileData?.name == doc.FileName || file.supplierDocument?.FileName == doc.FileName);

            if (file && file.effectiveDateControl) {
              doc.EffectiveDate = file.effectiveDateControl.value;
            } else {
              doc.EffectiveDate = doc['effectiveDate'] || doc.EffectiveDate;
            }

            if (file && file.expireDateControl) {
              doc.ExpiryDate = file.expireDateControl.value;
            } else {
              doc.ExpiryDate = doc['expiryDate'] || doc.ExpiryDate;
            }
          });

          //  Dispatch action to save the bank details
          //  There's a listener in the init action that will invoke the oncomplete event when
          //  a new post success action is dispatched.
          const body: SupplierBank = { ...supplierBank, IsSecurityCheckRequired: this.isSecurityCheckRequired };
          this.dialogRef.close(body);
        }
      }, err => this.setErrorMessage(err))

      //  Subscribe so that we can capture an error if any
      .subscribe(() => { }, err => this.setErrorMessage(err));
  }

  private sendValidationRequest(bank: SupplierBank): Observable<string[]> {
    //  SEND VALIDATION REQUEST ABOUT THE BANK DATA
    const validationRequest = new BankValidationRequest();
    validationRequest.bankCountryId = bank.CountryCode;
    //validationRequest.paymentOrigin = supplierBank.pay;   //  There's no payment origin in onboarding

    validationRequest.isNewBankAccount = bank.IsNewBankAccount;
    validationRequest.id = bank.ID;
    validationRequest.bankAccountId = !bank.StageID && (!bank.AccountNumberExists || bank.IsNewBankAccount) ? bank.AccountNumber : null;
    validationRequest.bankIBAN = !bank.StageID && (!bank.IBANCodeExists || bank.IsNewBankAccount) ? bank.IBANCode : null;

    validationRequest.bankSwift = bank.SwiftCode;
    validationRequest.bankRoutingCode = bank.ABANumber ? bank.ABANumber.toString() : '';

    validationRequest.beneficiaryNameEnglish = bank.BeneficiaryNameEnglish;
    validationRequest.beneficiaryNameLocalLanguage = bank.BeneficiaryNameLocal;

    validationRequest.paymentType = bank.PaymentType;
    validationRequest.bankAccountKatanaka = bank.BankAccountNameKatanaka;

    //  Address fields
    validationRequest.streetAddressLine1 = bank.Address1;
    validationRequest.streetAddressLine2 = bank.Address2;
    validationRequest.city = bank.City;
    validationRequest.state = bank.State;
    validationRequest.zipCode = bank.ZipCode?.toString();
    validationRequest.verificationStatus = bank.VerificationStatus;
    validationRequest.verificationTransactionId = bank.VerificationTransactionId;

    this.supplierStore.dispatch(new LoaderActions.IncrementLoaderCount());
    return this.supplierStateService.validateBankConfigurationWithoutOTP(this.supplierCode, validationRequest)
  }

  saveEventHandler(bank: SupplierBank) {

  }

  onSubmit() {
    this.bankEditComponent.onSubmit();
  }

  onClose() {
    this.dialogRef.close();
  }
}
